import { Boxes } from "@/components/ui/aceternity-ui/background-boxes";
import { FlipWords } from "@/components/ui/aceternity-ui/flip-words";
import { WorldMap } from "@/components/ui/aceternity-ui/world-map";
import { AnimatedBeam } from "@/components/ui/magicui/animated-beam-outline";
import { BentoCard, BentoGrid } from "@/components/ui/magicui/bento-grid";
import { Marquee } from "@/components/ui/magicui/marquee";
import { useMobileOptimized } from "@/hooks/useMobileOptimized";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  CpuIcon,
  Share2Icon as LucideShare2Icon,
  UsersIcon,
  ZapIcon,
} from "lucide-react";
import { useRef } from "react";

// Import AI Provider Icons
import { ClaudeIcon } from "@/components/Chat/header/icons/ClaudeIcon";
import { DeepSeekIcon as DeepSeekIconColored } from "@/components/Chat/header/icons/DeepSeekIcon";
import { GeminiIcon as GeminiIconColored } from "@/components/Chat/header/icons/GeminiIcon";
import { GrokIcon } from "@/components/Chat/header/icons/GrokIcon";
import { GroqIcon } from "@/components/Chat/header/icons/GroqIcon";
import { MistralIcon } from "@/components/Chat/header/icons/MistralIcon";
import { OpenAIIcon as OpenAIIconColored } from "@/components/Chat/header/icons/OpenAIIcon";

// Import integration icons
import { GmailIcon, NotionIcon, StabilityIcon } from "@/components";
import ArtifactLogo from "@/components/Chat/header/icons/ArtifactLogo";
import GitHubIcon from "@/components/Chat/header/icons/GitHubIcon";
import GoogleDriveIcon from "@/components/Chat/header/icons/GoogleDriveIcon";
import { MCPIcon } from "@/components/Chat/header/icons/MCPIcon";
import SlackIcon from "@/components/Chat/header/icons/SlackIcon";

// Simplified Integrations Animation
const IntegrationsBeamAnimation = ({
  className,
  shouldReduceMotion = false,
}) => {
  const containerRef = useRef(null);
  const artifactRef = useRef(null);
  const mcpRef = useRef(null);
  const githubRef = useRef(null);
  const slackRef = useRef(null);
  const driveRef = useRef(null);
  const notionRef = useRef(null);
  const gmailRef = useRef(null);
  const stabilityRef = useRef(null);

  const Node = ({
    icon: Icon,
    label,
    nodeRef,
    bgColor = "bg-primary/10",
    iconClassName = "text-primary",
    isCustomIcon = false,
  }) => (
    <div
      ref={nodeRef}
      className={cn(
        "z-9999 flex flex-col items-center justify-center w-20 h-20 p-2 rounded-lg shadow-lg text-xs relative",
        bgColor,
        "border border-primary/20"
      )}
    >
      {isCustomIcon ? (
        <div className="w-6 h-6 mb-1">
          <Icon className={cn("w-full h-full", iconClassName)} />
        </div>
      ) : (
        <Icon className={cn("w-6 h-6 mb-1", iconClassName)} />
      )}
      {label}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full h-full flex items-center justify-center",
        className
      )}
    >
      <div className="flex flex-row items-center gap-12 relative z-0">
        <Node
          icon={ArtifactLogo}
          label="Artifact"
          nodeRef={artifactRef}
          bgColor="bg-purple-500/10 dark:bg-purple-500/20"
          iconClassName="text-purple-500 dark:text-purple-400"
          isCustomIcon={true}
        />
        <Node
          icon={MCPIcon}
          label="MCP"
          nodeRef={mcpRef}
          bgColor="bg-blue-500/10 dark:bg-blue-500/20"
          iconClassName="text-blue-500 dark:text-blue-400"
          isCustomIcon={true}
        />
        <div className="flex flex-col gap-4">
          <Node icon={GitHubIcon} label="GitHub" nodeRef={githubRef} />
          <Node
            icon={SlackIcon}
            label="Slack"
            nodeRef={slackRef}
            isCustomIcon={true}
          />
          <Node
            icon={GoogleDriveIcon}
            label="Drive"
            nodeRef={driveRef}
            isCustomIcon={true}
          />
        </div>
        <div className="flex flex-col gap-4">
          <Node icon={NotionIcon} label="Notion" nodeRef={notionRef} />
          <Node
            icon={GmailIcon}
            label="Gmail"
            nodeRef={gmailRef}
            isCustomIcon={true}
          />
          <Node
            icon={StabilityIcon}
            label="Stability"
            nodeRef={stabilityRef}
            isCustomIcon={true}
          />
        </div>
      </div>

      {/* Only show animated beams if motion is not reduced */}
      {!shouldReduceMotion && (
        <>
          <AnimatedBeam
            duration={3}
            containerRef={containerRef}
            fromRef={artifactRef}
            toRef={mcpRef}
            gradientStartColor="#a855f7"
            gradientStopColor="#7c3aed"
          />
          <AnimatedBeam
            duration={3}
            delay={0.1}
            containerRef={containerRef}
            fromRef={mcpRef}
            toRef={githubRef}
            gradientStartColor="#3b82f6"
            gradientStopColor="#1d4ed8"
          />
          <AnimatedBeam
            duration={3}
            delay={0.2}
            containerRef={containerRef}
            fromRef={mcpRef}
            toRef={slackRef}
            gradientStartColor="#3b82f6"
            gradientStopColor="#1d4ed8"
          />
          <AnimatedBeam
            duration={3}
            delay={0.3}
            containerRef={containerRef}
            fromRef={mcpRef}
            toRef={driveRef}
            gradientStartColor="#3b82f6"
            gradientStopColor="#1d4ed8"
          />
        </>
      )}
    </div>
  );
};

const aiProviderIcons = [
  { name: "OpenAI", Icon: OpenAIIconColored },
  { name: "Claude", Icon: ClaudeIcon },
  { name: "Mistral", Icon: MistralIcon },
  { name: "DeepSeek", Icon: DeepSeekIconColored },
  {
    name: "Groq",
    Icon: ({ className, size }) => (
      <GroqIcon className={className} size={size} color="#038247" />
    ),
  },
  { name: "Gemini", Icon: GeminiIconColored },
  { name: "Grok", Icon: GrokIcon },
];

export function ProductHighlightSection() {
  const { shouldReduceMotion } = useMobileOptimized();

  // Create mobile-optimized product highlights
  const getProductHighlights = () => [
    {
      Icon: CpuIcon,
      name: "Versatile Models",
      description: "Access a diverse suite of cutting-edge AI models.",
      href: "/integrations",
      cta: "Explore Models",
      className: "col-span-3 lg:col-span-1",
      buttonClassName:
        "bg-white/80 backdrop-blur-sm hover:bg-purple-50 text-purple-700 border border-purple-200 shadow-sm dark:bg-transparent dark:hover:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/50",
      background: shouldReduceMotion ? (
        // Static grid for mobile
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-3 gap-4 opacity-30">
            {aiProviderIcons.slice(0, 6).map((provider, idx) => (
              <div
                key={idx}
                className={cn(
                  "relative w-16 h-16 cursor-pointer overflow-hidden rounded-xl border p-2 flex items-center justify-center",
                  "border-gray-950/[.1] bg-gray-950/[.01]",
                  "dark:border-gray-50/[.1] dark:bg-gray-50/[.10]"
                )}
              >
                <provider.Icon className="w-8 h-8" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Marquee
          pauseOnHover
          className="absolute top-10 [--duration:20s] [mask-image:linear-gradient(to_top,transparent_5%,#000_60%)]"
        >
          {aiProviderIcons.map((provider, idx) => (
            <div
              key={idx}
              className={cn(
                "relative w-20 h-20 cursor-pointer overflow-hidden rounded-xl border p-2 mx-2 flex items-center justify-center",
                "border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05]",
                "dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]",
                "transform-gpu blur-[0px] transition-all duration-300 ease-out"
              )}
            >
              <provider.Icon className="w-10 h-10" />
            </div>
          ))}
        </Marquee>
      ),
    },
    {
      Icon: UsersIcon,
      name: "Collaborate Seamlessly",
      description: "Shared projects and conversations for effortless teamwork.",
      href: "/solutions",
      cta: "Learn More",
      className: "col-span-3 lg:col-span-2",
      buttonClassName:
        "bg-white/80 backdrop-blur-sm hover:bg-purple-50 text-purple-700 border border-purple-200 shadow-sm dark:bg-transparent dark:hover:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/50",
      background: shouldReduceMotion ? (
        // Static world map for mobile
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground opacity-30">
          <div className="text-6xl">🌍</div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_10%,#000_100%)] group-hover:scale-95">
          <WorldMap
            lineColor="#a855f7"
            dots={[
              {
                start: { lat: 40.7128, lng: -74.006 }, // New York
                end: { lat: 51.5074, lng: -0.1278 }, // London
              },
              {
                start: { lat: 37.7749, lng: -122.4194 }, // San Francisco
                end: { lat: 35.6762, lng: 139.6503 }, // Tokyo
              },
            ]}
          />
        </div>
      ),
    },
    {
      Icon: LucideShare2Icon,
      name: "Powerful Integrations",
      description:
        "Connect with 100+ tools like GitHub, Slack, and Google Drive.",
      href: "/integrations",
      cta: "View Integrations",
      className: "col-span-3 lg:col-span-2",
      buttonClassName:
        "bg-white/80 backdrop-blur-sm hover:bg-purple-50 text-purple-700 border border-purple-200 shadow-sm dark:bg-transparent dark:hover:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/50",
      background: (
        <IntegrationsBeamAnimation
          className="absolute right-2 top-4 h-[300px] w-[calc(100%-1rem)] border-none transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_10%,#000_100%)] group-hover:scale-105"
          shouldReduceMotion={shouldReduceMotion}
        />
      ),
    },
    {
      Icon: ZapIcon,
      name: "Versatile Use Cases",
      description: "Perfect for professionals across all industries",
      href: "/solutions",
      cta: "Explore Use Cases",
      className: "col-span-3 lg:col-span-1",
      buttonClassName:
        "bg-white/80 backdrop-blur-sm hover:bg-purple-50 text-purple-700 border border-purple-200 shadow-sm dark:bg-transparent dark:hover:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/50",
      background: (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          {/* Background Boxes Animation - Reduced on mobile */}
          {!shouldReduceMotion && (
            <div className="absolute inset-0 opacity-20 dark:opacity-10">
              <Boxes className="dark:text-purple-500/20 text-purple-300/20" />
            </div>
          )}

          <div className="relative z-10 flex flex-col items-center justify-center gap-6 w-full max-w-full text-center">
            <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-violet-600">
              {shouldReduceMotion ? (
                "Professionals"
              ) : (
                <FlipWords
                  words={[
                    "Developers",
                    "Writers",
                    "Marketers",
                    "Designers",
                    "Researchers",
                  ]}
                  duration={2000}
                />
              )}
            </div>

            <div className="text-base font-medium text-foreground/80">
              {shouldReduceMotion ? (
                "Enhance productivity"
              ) : (
                <FlipWords
                  words={[
                    "Write better code",
                    "Overcome writer's block",
                    "Create engaging content",
                    "Generate creative concepts",
                    "Analyze complex data",
                  ]}
                  duration={2500}
                />
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="py-16 sm:py-24 bg-background/80 backdrop-blur-sm">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            Powerful, Connected,{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-violet-500 to-purple-600">
              Always Evolving
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover how Artifact integrates cutting-edge AI, collaborative
            features, and a universe of tools to supercharge your workflow.
          </p>
        </motion.div>
        <BentoGrid>
          {getProductHighlights().map((feature, idx) => (
            <BentoCard key={idx} {...feature} />
          ))}
        </BentoGrid>
      </div>
    </section>
  );
}

export default ProductHighlightSection;
